// src/components/administration/manifest/ManifestTripMonitor.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Box, Card, CardContent, Typography, Switch,
  FormControlLabel, Divider, Stack, Chip, IconButton, Tooltip,
  Alert, Button, Paper, LinearProgress, Avatar,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import TimelineIcon from "@mui/icons-material/Timeline";
import PlaceIcon from "@mui/icons-material/Place";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RouteIcon from "@mui/icons-material/Route";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SignalCellularOffIcon from "@mui/icons-material/SignalCellularOff";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

import { useRutasManifiesto } from "../../../hooks/useManifest";
import {
  useManifestTrackHistory,
  useManifestLocationStream,
  useManifestPlannedRoute,
  useManifestLiveRoute,
  useManifestLiveRouteStats,
  useManifestLiveRouteEta,
  useManifestMatchedTrack,
  useManifestInfo,
  classifySpeed,
} from "../../../hooks/gpsTracking/useGpsTracking";
import { useQueryClient } from "@tanstack/react-query";
import { buildTripLayers } from "../../../utils/gpsAnalysis";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const TZ = "America/Lima";
const EMPTY_FC = { type: "FeatureCollection", features: [] };

/* ---------- formatters ---------- */
const fmtKm = (n) => (Number.isFinite(n) ? `${n.toFixed(2)} km` : "—");
const fmtKmh = (n) => (Number.isFinite(n) ? `${n.toFixed(1)} km/h` : "—");
const fmtMin = (sec) => (Number.isFinite(sec) ? `${(sec / 60).toFixed(0)} min` : "—");
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-PE", { timeZone: TZ, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("es-PE", { timeZone: TZ, hour: "2-digit", minute: "2-digit" });
};
const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  if (diff < 0) return "ahora";
  if (diff < 60_000) return "<1 min";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h`;
  return d.toLocaleString("es-PE", { timeZone: TZ });
};
const initials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};
const safeSetVisibility = (map, layerId, visible) => {
  if (!map || !map.getLayer(layerId)) return;
  try { map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none"); } catch { }
};

const SPEED_COLORS = {
  stop: '#9CA3AF', slow: '#60A5FA', normal: '#22C55E',
  fast: '#F59E0B', speeding: '#EF4444', critical: '#7F1D1D',
};
const SPEED_LABELS = {
  stop: 'Detenido', slow: 'Lento', normal: 'Normal',
  fast: 'Rápido', speeding: 'Exceso', critical: 'Crítico',
};

const ManifestTripMonitor = () => {
  const { manid } = useParams();
  const manifestId = Number(manid);
  const queryClient = useQueryClient();

  const [isMonitoring, setIsMonitoring] = useState(true);
  const [showPlanned, setShowPlanned] = useState(true);
  const [showLive, setShowLive] = useState(true);
  const [showRealRoute, setShowRealRoute] = useState(true);
  const [showSegments, setShowSegments] = useState(true);  // ← default TRUE
  const [showPoints, setShowPoints] = useState(true);       // ← default TRUE
  const [showStops, setShowStops] = useState(true);
  const [showGapMarkers, setShowGapMarkers] = useState(true);
  const [showOutliers, setShowOutliers] = useState(false);

  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const vehicleMarkerRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);

  const { paradas } = useRutasManifiesto(manifestId);
  const paradasActuales = useMemo(() => paradas ?? [], [paradas]);
  const hasEnoughStops = paradasActuales.length >= 2;

  const { data: info } = useManifestInfo(manifestId, {
    enabled: !!manifestId,
    refetchInterval: 30_000,
  });

  const isCompleted = info?.manEst === 'COMPLETADO' || info?.manEst === 'CANCELADO';
  const isInProcess = info?.manEst === 'EN_PROCESO';

  const { data: history } = useManifestTrackHistory(manifestId, {
    enabled: !!manifestId,
    refetchInterval: isMonitoring && isInProcess ? 30_000 : false,
  });

  const { data: plannedRoute } = useManifestPlannedRoute(manifestId, !!manifestId);
  const { data: liveRoute } = useManifestLiveRoute(manifestId, { enabled: !!manifestId });
  const { data: liveStats } = useManifestLiveRouteStats(manifestId, { enabled: !!manifestId });
  const { data: liveEta } = useManifestLiveRouteEta(manifestId, {
    enabled: !!manifestId && isInProcess,
    refetchInterval: isMonitoring && isInProcess ? 15_000 : false,
  });
  const { data: matchedTrack } = useManifestMatchedTrack(manifestId, {
    enabled: !!manifestId,
    format: "feature",
    refetchInterval: isMonitoring && isInProcess ? 60_000 : false,
  });

  const { lastLoc, lastRouteEvent, status: streamStatus, error: streamError } =
    useManifestLocationStream({
      manid: manifestId,
      enabled: !!manifestId && isMonitoring && isInProcess,
    });

  useEffect(() => {
    if (!lastRouteEvent) return;
    queryClient.invalidateQueries({ queryKey: ["gps-manifest-live-route", manifestId] });
    queryClient.invalidateQueries({ queryKey: ["gps-manifest-live-route-stats", manifestId] });
    queryClient.invalidateQueries({ queryKey: ["gps-manifest-live-route-eta", manifestId] });
  }, [lastRouteEvent, manifestId, queryClient]);

  const usingMatched = useMemo(() => {
    if (!matchedTrack) return false;
    if (matchedTrack.format === "feature") {
      const conf = matchedTrack.meta?.confidence ?? 0;
      return conf >= 0.1 && (matchedTrack.features?.length ?? 0) > 0;
    }
    return false;
  }, [matchedTrack]);

  const mergedHistory = useMemo(() => {
    const base = Array.isArray(history) ? history : [];
    if (!lastLoc || typeof lastLoc.lat !== "number" || typeof lastLoc.lng !== "number") return base;
    const last = base[base.length - 1];
    if (last) {
      const sameCoord = Math.abs(Number(last.lat) - lastLoc.lat) < 1e-6
        && Math.abs(Number(last.lng) - lastLoc.lng) < 1e-6;
      if (sameCoord) return base;
    }
    return [...base, { lat: lastLoc.lat, lng: lastLoc.lng, timestamp: lastLoc.timestamp }];
  }, [history, lastLoc]);

  const layers = useMemo(() => {
    return buildTripLayers(mergedHistory || [], {
      timeZone: TZ, gapSec: 900, gapKm: 2.0, maxKmh: 130,
      stopDistKm: 0.015, stopMinSec: 60,
      simplify: { enabled: true, maxPoints: 1500, toleranceKm: 0.02 },
    });
  }, [mergedHistory]);

  const { pointsFC, tracksFC, stopsFC, outliersFC, stats: rawTripStats } = layers;

  const baseMatchedFC = useMemo(() => {
    if (matchedTrack?.format === "feature" && Array.isArray(matchedTrack.features)) {
      return { type: "FeatureCollection", features: matchedTrack.features };
    }
    return EMPTY_FC;
  }, [matchedTrack]);

  const realSegmentsFC = useMemo(() => {
    if (usingMatched) return baseMatchedFC;
    return tracksFC || EMPTY_FC;
  }, [usingMatched, baseMatchedFC, tracksFC]);

  const realTrackFC = useMemo(() => {
    if (usingMatched) {
      return {
        type: "FeatureCollection",
        features: baseMatchedFC.features.map((f) => ({
          ...f, properties: { ...f.properties, color: null },
        })),
      };
    }
    return tracksFC || EMPTY_FC;
  }, [usingMatched, baseMatchedFC, tracksFC]);

  const gapMarkersFC = useMemo(() => {
    if (usingMatched && Array.isArray(matchedTrack?.gaps) && matchedTrack.gaps.length > 0) {
      const features = [];
      for (const g of matchedTrack.gaps) {
        if (g.from) features.push({
          type: "Feature",
          properties: { kind: "head", reason: g.reason, durationSec: g.durationSec },
          geometry: { type: "Point", coordinates: [g.from.lng, g.from.lat] },
        });
        if (g.to) features.push({
          type: "Feature",
          properties: { kind: "tail", reason: g.reason, durationSec: g.durationSec },
          geometry: { type: "Point", coordinates: [g.to.lng, g.to.lat] },
        });
      }
      return { type: "FeatureCollection", features };
    }
    return EMPTY_FC;
  }, [usingMatched, matchedTrack]);

  const stats = useMemo(() => {
    if (usingMatched && matchedTrack?.stats) {
      const s = matchedTrack.stats;
      return {
        distKm: (s.totalDistanceMeters || 0) / 1000,
        durationSec: s.totalDurationSec || 0,
        avgSpeedKmh: s.avgSpeedKmh || 0,
        maxSpeedKmh: s.maxSpeedKmh || 0,
        speedingPct: s.speedingPct || 0,
        gapsCount: s.gapCount || 0,
        classCounts: s.classCounts || {},
      };
    }
    if (rawTripStats) {
      return {
        distKm: rawTripStats.distKm,
        durationSec: rawTripStats.durationSec,
        avgSpeedKmh: rawTripStats.avgSpeedKmh,
        maxSpeedKmh: rawTripStats.maxSpeedKmh,
        speedingPct: null,
        gapsCount: rawTripStats.tracksCount ? Math.max(0, rawTripStats.tracksCount - 1) : 0,
        classCounts: null,
      };
    }
    return null;
  }, [usingMatched, matchedTrack, rawTripStats]);

  const classLegend = useMemo(() => {
    const cc = stats?.classCounts;
    if (!cc || typeof cc !== "object") return [];
    return Object.entries(cc)
      .filter(([cls]) => SPEED_COLORS[cls])
      .sort((a, b) => b[1] - a[1])
      .map(([cls, count]) => ({ cls, count, color: SPEED_COLORS[cls], label: SPEED_LABELS[cls] || cls }));
  }, [stats]);

  const currentSpeed = lastLoc?.speedKmh;
  const currentSpeedClass = classifySpeed(currentSpeed);
  const isSpeedingNow = Number.isFinite(currentSpeed) && currentSpeed >= 90;

  const fitToPlannedBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !paradasActuales?.length) return;
    const bounds = new mapboxgl.LngLatBounds();
    paradasActuales.forEach((p) => bounds.extend([p.loclng, p.loclat]));
    map.fitBounds(bounds, { padding: 70, duration: 800 });
  }, [paradasActuales]);

  const fitToRealTripBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !mergedHistory?.length) return;
    const bounds = new mapboxgl.LngLatBounds();
    mergedHistory.forEach((p) => bounds.extend([p.lng, p.lat]));
    if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 70, duration: 800 });
  }, [mergedHistory]);

  const centerToLastLoc = useCallback(() => {
    const map = mapRef.current;
    if (!map || !lastLoc) return;
    map.flyTo({ center: [lastLoc.lng, lastLoc.lat], zoom: 15, speed: 1.2 });
  }, [lastLoc]);

  /* Init del mapa */
  useEffect(() => {
    if (!mapContainer.current || !hasEnoughStops) return;
    setIsMapReady(false);
    if (mapRef.current) { try { mapRef.current.remove(); } catch { } mapRef.current = null; }
    if (vehicleMarkerRef.current) { try { vehicleMarkerRef.current.remove(); } catch { } vehicleMarkerRef.current = null; }

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [paradasActuales[0].loclng, paradasActuales[0].loclat],
      zoom: 12,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    map.on("load", () => {
      const bounds = new mapboxgl.LngLatBounds();
      paradasActuales.forEach((loc, idx) => {
        bounds.extend([loc.loclng, loc.loclat]);
        new mapboxgl.Marker({ color: "#0ea5e9" })
          .setLngLat([loc.loclng, loc.loclat])
          .setPopup(new mapboxgl.Popup().setHTML(
            `<div style="min-width:180px"><strong>${idx + 1}. ${loc.locnom}</strong></div>`
          ))
          .addTo(map);
      });
      map.fitBounds(bounds, { padding: 70 });

      map.addSource("planned-route-src", { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "planned-route", type: "line", source: "planned-route-src",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-width": 6, "line-opacity": 0.85, "line-color": "#1e3a8a" },
      });

      map.addSource("live-route-src", { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "live-route", type: "line", source: "live-route-src",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-width": 6, "line-opacity": 0.95, "line-color": "#f97316", "line-dasharray": [2, 2] },
      });

      map.addSource("real-track-src", { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "real-track", type: "line", source: "real-track-src",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-width": 4, "line-opacity": 0.85, "line-color": "#475569" },
      });

      map.addSource("real-segments-src", { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "real-segments", type: "line", source: "real-segments-src",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-width": 6, "line-opacity": 0.9,
          "line-color": [
            "coalesce", ["get", "color"],
            ["case", ["has", "speedKmh"],
              ["case",
                ["<", ["get", "speedKmh"], 3], "#9CA3AF",
                ["<", ["get", "speedKmh"], 30], "#60A5FA",
                ["<", ["get", "speedKmh"], 70], "#22C55E",
                ["<", ["get", "speedKmh"], 90], "#F59E0B",
                ["<", ["get", "speedKmh"], 120], "#EF4444",
                "#7F1D1D",
              ],
              "#0f172a",
            ],
          ],
        },
      });

      map.addSource("trip-points-src", { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "trip-points", type: "circle", source: "trip-points-src",
        paint: {
          "circle-radius": 3.5, "circle-color": "#22d3ee",
          "circle-opacity": 0.85, "circle-stroke-width": 1, "circle-stroke-color": "#ffffff",
        },
      });
      map.on("click", "trip-points", (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const { idx, timeLabel } = f.properties || {};
        new mapboxgl.Popup().setLngLat(e.lngLat).setHTML(
          `<div style="min-width:180px"><strong>Ping #${idx}</strong><br/><small>${timeLabel}</small></div>`
        ).addTo(map);
      });
      map.on("mouseenter", "trip-points", () => (map.getCanvas().style.cursor = "pointer"));
      map.on("mouseleave", "trip-points", () => (map.getCanvas().style.cursor = ""));

      map.addSource("gap-markers-src", { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "gap-markers", type: "circle", source: "gap-markers-src",
        paint: {
          "circle-radius": 5,
          "circle-color": [
            "case",
            ["==", ["get", "reason"], "time"], "#f59e0b",
            ["==", ["get", "reason"], "distance"], "#ef4444",
            "#f59e0b",
          ],
          "circle-opacity": 0.9, "circle-stroke-width": 1.5, "circle-stroke-color": "#ffffff",
        },
      });
      map.on("click", "gap-markers", (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const { kind, reason, durationSec } = f.properties || {};
        new mapboxgl.Popup().setLngLat(e.lngLat).setHTML(
          `<div style="min-width:200px">
            <strong>${kind === "head" ? "Inicio" : "Fin"} de corte</strong><br/>
            <small>Motivo: ${reason === "time" ? "Sin señal > 5 min" : "Salto de distancia"}</small><br/>
            <small>Duración: ${durationSec ? Math.round(durationSec / 60) + " min" : "—"}</small>
          </div>`
        ).addTo(map);
      });

      map.addSource("trip-stops-src", { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "trip-stops", type: "circle", source: "trip-stops-src",
        paint: {
          "circle-radius": 7, "circle-color": "#a855f7",
          "circle-opacity": 0.95, "circle-stroke-width": 2, "circle-stroke-color": "#ffffff",
        },
      });

      map.addSource("trip-outliers-src", { type: "geojson", data: EMPTY_FC });
      map.addLayer({
        id: "trip-outliers", type: "circle", source: "trip-outliers-src",
        paint: {
          "circle-radius": 6, "circle-color": "#db2777",
          "circle-opacity": 0.95, "circle-stroke-width": 2, "circle-stroke-color": "#ffffff",
        },
      });

      setIsMapReady(true);
    });

    return () => {
      try { map.remove(); } catch { }
      mapRef.current = null;
      setIsMapReady(false);
    };
  }, [paradasActuales, hasEnoughStops]);

  useEffect(() => {
    const m = mapRef.current; if (!m || !isMapReady) return;
    m.getSource("planned-route-src")?.setData(
      plannedRoute?.geometry ? { type: "Feature", properties: {}, geometry: plannedRoute.geometry } : EMPTY_FC
    );
  }, [isMapReady, plannedRoute]);

  useEffect(() => {
    const m = mapRef.current; if (!m || !isMapReady) return;
    m.getSource("live-route-src")?.setData(
      liveRoute?.geometry ? { type: "Feature", properties: {}, geometry: liveRoute.geometry } : EMPTY_FC
    );
  }, [isMapReady, liveRoute]);

  useEffect(() => {
    const m = mapRef.current; if (!m || !isMapReady) return;
    m.getSource("real-track-src")?.setData(realTrackFC);
  }, [isMapReady, realTrackFC]);

  useEffect(() => {
    const m = mapRef.current; if (!m || !isMapReady) return;
    m.getSource("real-segments-src")?.setData(realSegmentsFC);
  }, [isMapReady, realSegmentsFC]);

  useEffect(() => {
    const m = mapRef.current; if (!m || !isMapReady) return;
    m.getSource("trip-points-src")?.setData(pointsFC || EMPTY_FC);
  }, [isMapReady, pointsFC]);

  useEffect(() => {
    const m = mapRef.current; if (!m || !isMapReady) return;
    m.getSource("trip-stops-src")?.setData(stopsFC || EMPTY_FC);
  }, [isMapReady, stopsFC]);

  useEffect(() => {
    const m = mapRef.current; if (!m || !isMapReady) return;
    m.getSource("trip-outliers-src")?.setData(outliersFC || EMPTY_FC);
  }, [isMapReady, outliersFC]);

  useEffect(() => {
    const m = mapRef.current; if (!m || !isMapReady) return;
    m.getSource("gap-markers-src")?.setData(gapMarkersFC || EMPTY_FC);
  }, [isMapReady, gapMarkersFC]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    const showRealTrack = showRealRoute && !showSegments;
    const showRealSegments = showRealRoute && showSegments;
    safeSetVisibility(map, "planned-route", showPlanned);
    safeSetVisibility(map, "live-route", showLive && !isCompleted);
    safeSetVisibility(map, "real-track", showRealTrack);
    safeSetVisibility(map, "real-segments", showRealSegments);
    safeSetVisibility(map, "trip-points", showPoints);
    safeSetVisibility(map, "gap-markers", showGapMarkers && showRealRoute);
    safeSetVisibility(map, "trip-stops", showStops);
    safeSetVisibility(map, "trip-outliers", showOutliers);
  }, [isMapReady, showPlanned, showLive, showRealRoute, showSegments, showGapMarkers, showStops, showOutliers, showPoints, isCompleted]);

  useEffect(() => {
    if (!isMonitoring || !lastLoc || isCompleted) return;
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    const { lat, lng, timestamp, speedKmh } = lastLoc;
    const popupHtml = `
      <div style="min-width:200px">
        <strong>Última posición</strong><br/>
        ${Number.isFinite(speedKmh) ? `<small>Velocidad: <b>${Math.round(speedKmh)} km/h</b></small><br/>` : ''}
        <small>${new Date(timestamp).toLocaleString("es-PE", { timeZone: TZ })}</small>
      </div>`;
    if (vehicleMarkerRef.current) {
      vehicleMarkerRef.current.setLngLat([lng, lat]);
      vehicleMarkerRef.current.getPopup()?.setHTML(popupHtml);
      return;
    }
    vehicleMarkerRef.current = new mapboxgl.Marker({ color: "#ef4444" })
      .setLngLat([lng, lat]).setPopup(new mapboxgl.Popup().setHTML(popupHtml)).addTo(map);
  }, [lastLoc, isMonitoring, isMapReady, isCompleted]);

  useEffect(() => {
    if (isMonitoring && !isCompleted) return;
    if (vehicleMarkerRef.current) { try { vehicleMarkerRef.current.remove(); } catch { } vehicleMarkerRef.current = null; }
  }, [isMonitoring, isCompleted]);

  const stopsTotal = paradasActuales.length;
  const stopsVisited = paradasActuales.filter((p) => p.manparvis).length;
  const progressPct = stopsTotal > 0 ? Math.round((stopsVisited / stopsTotal) * 100) : 0;

  const statusChip = useMemo(() => {
    const manEst = info?.manEst;
    if (manEst === 'COMPLETADO') return { label: 'COMPLETADO', color: 'success', icon: <CheckCircleIcon fontSize="small" /> };
    if (manEst === 'CANCELADO') return { label: 'CANCELADO', color: 'error', icon: <CancelIcon fontSize="small" /> };
    if (manEst === 'EN_PROCESO') return { label: 'EN PROCESO', color: 'info', icon: <PlayCircleIcon fontSize="small" /> };
    if (manEst === 'PLANIFICADO') return { label: 'PLANIFICADO', color: 'default', icon: null };
    if (manEst === 'CREADO') return { label: 'CREADO', color: 'default', icon: null };
    return { label: manEst || '—', color: 'default', icon: null };
  }, [info?.manEst]);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 2, height: "calc(100vh - 140px)" }}>
      <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", boxShadow: 1, bgcolor: "grey.100" }}>
        <Box ref={mapContainer} sx={{ width: "100%", height: "100%" }} />

        {!hasEnoughStops && (
          <Paper elevation={2} sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", p: 2, maxWidth: 320, textAlign: "center" }}>
            <Typography variant="subtitle2" gutterBottom>Sin paradas para mostrar</Typography>
            <Typography variant="caption" color="text.secondary">
              Este manifiesto no tiene paradas válidas configuradas.
            </Typography>
          </Paper>
        )}

        <Box sx={{ position: "absolute", top: 12, left: 12, display: "flex", flexDirection: "column", gap: 1 }}>
          <Tooltip title="Centrar en ruta real" placement="right">
            <span>
              <IconButton size="small" onClick={fitToRealTripBounds} disabled={!mergedHistory?.length} sx={{ bgcolor: "white", boxShadow: 1 }}>
                <CenterFocusStrongIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Centrar en ruta programada" placement="right">
            <span>
              <IconButton size="small" onClick={fitToPlannedBounds} disabled={!paradasActuales?.length} sx={{ bgcolor: "white", boxShadow: 1 }}>
                <TimelineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {!isCompleted && (
            <Tooltip title="Ir a la última ubicación" placement="right">
              <span>
                <IconButton size="small" onClick={centerToLastLoc} disabled={!lastLoc} sx={{ bgcolor: "white", boxShadow: 1 }}>
                  <MyLocationIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Card sx={{ height: "100%", borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "auto", p: 2, "&:last-child": { pb: 2 } }}>
          {/* Header */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocalShippingIcon sx={{ color: "primary.main" }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: "monospace", lineHeight: 1 }}>
                    {info?.vehMatricula || `Manifiesto #${manifestId}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">#{manifestId}</Typography>
                </Box>
              </Stack>
              <Chip size="small" label={statusChip.label} color={statusChip.color} icon={statusChip.icon} />
            </Stack>

            {info?.empNombre && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: isCompleted ? "grey.500" : "success.main", fontSize: 11, fontWeight: 800 }}>
                  {initials(info.empNombre)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {info.empNombreCorto || info.empNombre}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {info.rol || "—"} {info.hasActiveDriver ? 'al mando' : '(último)'}
                  </Typography>
                </Box>
              </Stack>
            )}

            {!isCompleted && currentSpeedClass && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25, p: 1, borderRadius: 1, bgcolor: currentSpeedClass.bg, border: `1px solid ${currentSpeedClass.color}30` }}>
                {isSpeedingNow ? <FlashOnIcon sx={{ color: currentSpeedClass.color, fontSize: 20 }} /> : <SpeedIcon sx={{ color: currentSpeedClass.color, fontSize: 20 }} />}
                <Typography variant="h5" sx={{ fontWeight: 900, color: currentSpeedClass.color, lineHeight: 1 }}>
                  {Math.round(currentSpeed)}
                </Typography>
                <Typography variant="caption" sx={{ color: currentSpeedClass.color, fontWeight: 700 }}>
                  km/h · {currentSpeedClass.label}
                </Typography>
              </Stack>
            )}

            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.25 }}>
              <RouteIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {info?.manOri || plannedRoute?.manOri || "—"} → {info?.manDes || plannedRoute?.manDes || "—"}
              </Typography>
            </Stack>

            {isInProcess && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.25 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <PauseCircleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Streaming en vivo</Typography>
                </Stack>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Switch size="small" checked={isMonitoring} onChange={(e) => setIsMonitoring(e.target.checked)} />}
                  label={<Typography variant="caption">{isMonitoring ? 'Activo' : 'Pausado'}</Typography>}
                />
              </Stack>
            )}

            {isInProcess && isMonitoring && (
              <Chip
                size="small" sx={{ mt: 1 }}
                color={streamStatus === "open" ? "success" : streamStatus === "error" ? "error" : "default"}
                icon={streamStatus === "open" ? <SignalCellularAltIcon /> : <SignalCellularOffIcon />}
                label={streamStatus === "open" ? `En vivo${lastLoc ? ` · ${fmtRelative(lastLoc.timestamp)}` : ""}` : streamStatus === "error" ? "Sin conexión" : "Conectando…"}
              />
            )}

            {isCompleted && info?.manFecFin && (
              <Alert severity="success" sx={{ mt: 1.25, py: 0.5 }}>
                <Typography variant="caption" component="div">
                  <strong>Viaje finalizado</strong> · {fmtDateTime(info.manFecFin)}
                </Typography>
              </Alert>
            )}

            {streamError && !isCompleted && <Alert severity="error" sx={{ mt: 1, py: 0.25 }}>{streamError}</Alert>}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>Progreso</Typography>
              <Typography variant="caption" color="text.secondary">{stopsVisited}/{stopsTotal} paradas</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={progressPct} sx={{ height: 8, borderRadius: 1, mt: 0.5 }} color={progressPct === 100 ? "success" : "primary"} />
            {!isCompleted && liveEta && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="baseline">
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{fmtTime(liveEta.etaAtFinal)}</Typography>
                <Typography variant="caption" color="text.secondary">ETA final · {fmtMin(liveEta.remainingSec)}</Typography>
              </Stack>
            )}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
              {isCompleted ? 'Histórico' : 'Ahora'}
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.75 }}>
              {!isCompleted && lastLoc && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <MyLocationIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="body2">{lastLoc.lat?.toFixed(5)}, {lastLoc.lng?.toFixed(5)}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtRelative(lastLoc.timestamp)}</Typography>
                </Stack>
              )}
              {!isCompleted && liveRoute?.nextStopId && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <PlaceIcon sx={{ fontSize: 14, color: "primary.main" }} />
                  <Typography variant="body2">
                    Próxima: <strong>#{liveRoute.nextStopId}</strong>
                    {liveEta?.durationToNextSec && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        (~{fmtMin(liveEta.durationToNextSec)})
                      </Typography>
                    )}
                  </Typography>
                </Stack>
              )}
              {isCompleted && info?.speedStats?.firstPointAt && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="body2">Inicio: <strong>{fmtTime(info.speedStats.firstPointAt)}</strong></Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="body2">Fin: <strong>{fmtTime(info.speedStats.lastPointAt)}</strong></Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>

          {stats && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>Métricas del viaje</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PlaceIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="body2">Distancia</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>{fmtKm(stats.distKm)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <SpeedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="body2">Vel. prom.</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>{fmtKmh(stats.avgSpeedKmh)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <SpeedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="body2">Vel. máx.</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>{fmtKmh(stats.maxSpeedKmh)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="body2">Duración</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>{fmtMin(stats.durationSec)}</Typography>
                  </Stack>
                  {stats.gapsCount > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ReportProblemIcon sx={{ fontSize: 14, color: "warning.main" }} />
                        <Typography variant="body2" color="warning.dark">Cortes</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={700} color="warning.dark">{stats.gapsCount}</Typography>
                    </Stack>
                  )}
                </Stack>
                {classLegend.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                    {classLegend.map((l) => (
                      <Chip key={l.cls} size="small" label={`${l.label}: ${l.count}`}
                        sx={{ bgcolor: l.color, color: "white", fontWeight: 600, fontSize: 10, height: 20 }} />
                    ))}
                  </Stack>
                )}
              </Box>
            </>
          )}

          {isCompleted && info?.speedStats?.speedingPoints > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Auditoría de velocidad</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <FlashOnIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography variant="body2" color="warning.dark">Excesos (&gt;90)</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700} color="warning.dark">
                      {info.speedStats.speedingPoints}
                    </Typography>
                  </Stack>
                  {info.speedStats.criticalPoints > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <FlashOnIcon sx={{ fontSize: 14, color: 'error.main' }} />
                        <Typography variant="body2" color="error.dark">Críticos (&gt;120)</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={700} color="error.dark">
                        {info.speedStats.criticalPoints}
                      </Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Vel. máx registrada</Typography>
                    <Typography variant="caption" fontWeight={700}>{fmtKmh(info.speedStats.maxSpeedKmh)}</Typography>
                  </Stack>
                </Stack>
              </Box>
            </>
          )}

          {info?.roleHistory?.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                    {isCompleted ? 'Conductores' : 'Turnos'}
                  </Typography>
                  <Chip size="small" label={`${info.totalDrivers} conductor${info.totalDrivers > 1 ? 'es' : ''}`} variant="outlined" />
                </Stack>
                <Stack spacing={1} sx={{ mt: 0.75 }}>
                  {info.roleHistory.map((h) => {
                    const isActive = !h.endAt;
                    return (
                      <Stack key={h.id} direction="row" alignItems="flex-start" spacing={1}
                        sx={{
                          p: 0.75, borderRadius: 1, bgcolor: isActive ? alpha('#22C55E', 0.05) : 'transparent',
                          border: isActive ? '1px solid' : '1px solid transparent', borderColor: isActive ? 'success.light' : 'transparent'
                        }}>
                        <Avatar sx={{
                          width: 24, height: 24, fontSize: 10, fontWeight: 800,
                          bgcolor: h.rol === 'DRIVER' ? 'success.main' : h.rol === 'ASSISTANT' ? 'info.main' : 'grey.500'
                        }}>
                          {initials(h.nombreCompleto)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" fontWeight={700} noWrap>{h.nombreCorto}</Typography>
                            <Chip size="small" label={h.rol}
                              color={h.rol === 'DRIVER' ? 'success' : h.rol === 'ASSISTANT' ? 'info' : 'default'}
                              variant="outlined" sx={{ height: 14, fontSize: 8, fontWeight: 700 }} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
                            {fmtTime(h.startAt)} → {h.endAt ? fmtTime(h.endAt) : 'al mando'}
                            {h.durationSec > 0 && ` · ${fmtMin(h.durationSec)}`}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>Capas</Typography>
            <Stack spacing={0.25} sx={{ mt: 0.75 }}>
              <FormControlLabel control={<Switch size="small" checked={showPlanned} onChange={(e) => setShowPlanned(e.target.checked)} />}
                label={<Typography variant="body2">Ruta programada</Typography>} />
              {!isCompleted && (
                <FormControlLabel control={<Switch size="small" checked={showLive} onChange={(e) => setShowLive(e.target.checked)} />}
                  label={<Typography variant="body2">Navegación viva</Typography>} />
              )}
              <FormControlLabel control={<Switch size="small" checked={showRealRoute} onChange={(e) => setShowRealRoute(e.target.checked)} />}
                label={<Typography variant="body2">Ruta real</Typography>} />
              <FormControlLabel
                control={<Switch size="small" checked={showSegments} onChange={(e) => setShowSegments(e.target.checked)} disabled={!showRealRoute} />}
                label={<Typography variant="body2">Colorear por velocidad</Typography>}
              />
              <FormControlLabel control={<Switch size="small" checked={showPoints} onChange={(e) => setShowPoints(e.target.checked)} />}
                label={<Typography variant="body2">Pings GPS</Typography>} />
              <FormControlLabel control={<Switch size="small" checked={showStops} onChange={(e) => setShowStops(e.target.checked)} />}
                label={<Typography variant="body2">Paradas detectadas</Typography>} />
              <FormControlLabel control={<Switch size="small" checked={showGapMarkers} onChange={(e) => setShowGapMarkers(e.target.checked)} />}
                label={<Typography variant="body2">Marcar cortes</Typography>} />
              <FormControlLabel control={<Switch size="small" checked={showOutliers} onChange={(e) => setShowOutliers(e.target.checked)} />}
                label={<Typography variant="body2">Puntos atípicos</Typography>} />
            </Stack>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>Acciones</Typography>
            <Stack spacing={1} sx={{ mt: 0.75 }}>
              <Button size="small" variant="outlined" startIcon={<CenterFocusStrongIcon />} onClick={fitToRealTripBounds} disabled={!mergedHistory?.length}>
                Centrar viaje real
              </Button>
              <Button size="small" variant="outlined" startIcon={<TimelineIcon />} onClick={fitToPlannedBounds} disabled={!paradasActuales?.length}>
                Centrar programado
              </Button>
              {!isCompleted && (
                <Button size="small" variant="outlined" startIcon={<MyLocationIcon />} onClick={centerToLastLoc} disabled={!lastLoc}>
                  Ir a última ubicación
                </Button>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManifestTripMonitor;